/**
 * Host-side sanity checks (pure; unit-tested in smoke/net.ts). The host runs
 * these on every client request; a failure sends the offender a correction
 * (block_rej / pos correction) and bumps its violation score.
 */

import { PLAYER_EYE, REACH } from '../constants';

/** Vanilla survival reach is 4.5 (we use REACH = 5.5); allow up to 6 from the eyes. */
export const MAX_REACH = Math.max(6, REACH + 0.5);

/** Distance from the eye to the nearest point of block (bx, by, bz)'s unit cube. */
export function reachDistance(ex: number, ey: number, ez: number, bx: number, by: number, bz: number): number {
  const dx = Math.max(bx - ex, 0, ex - (bx + 1));
  const dy = Math.max(by - ey, 0, ey - (by + 1));
  const dz = Math.max(bz - ez, 0, ez - (bz + 1));
  return Math.hypot(dx, dy, dz);
}

export function withinReach(feetX: number, feetY: number, feetZ: number, bx: number, by: number, bz: number): boolean {
  return reachDistance(feetX, feetY + PLAYER_EYE, feetZ, bx, by, bz) <= MAX_REACH;
}

/**
 * Max horizontal blocks per game tick. Sprint-jumping on ice with speed is
 * about 0.9; creative sprint-flying about 1.1. Elytra/riding don't exist yet.
 */
export const MAX_H_SPEED = 1.4;
/** Terminal fall speed is 3.92 blocks/tick; rising is bounded by jumps/flight. */
export const MAX_V_DOWN = 4.2;
export const MAX_V_UP = 1.5;
/** Slack added to each check (lag spikes bunch up packets). */
export const MOVE_SLACK = 1.25;

/**
 * Is moving from a to b over `ticks` game ticks plausible? `ticks` is clamped
 * to at least 1 and at most 40 so a long silence can't be used to teleport.
 */
export function plausibleMove(
  ax: number, ay: number, az: number, bx: number, by: number, bz: number, ticks: number,
): boolean {
  const t = Math.max(1, Math.min(40, ticks));
  const h = Math.hypot(bx - ax, bz - az);
  const dy = by - ay;
  if (h > MAX_H_SPEED * t * MOVE_SLACK + 0.5) return false;
  if (dy > MAX_V_UP * t * MOVE_SLACK + 0.6) return false;
  if (-dy > MAX_V_DOWN * t * MOVE_SLACK + 1) return false;
  return true;
}

/** Token bucket rate limiter. */
export class RateLimiter {
  private tokens: number;
  private last: number;
  constructor(private readonly perSec: number, private readonly burst: number, now = 0) {
    this.tokens = burst;
    this.last = now;
  }
  take(now: number, n = 1): boolean {
    this.tokens = Math.min(this.burst, this.tokens + (now - this.last) / 1000 * this.perSec);
    this.last = now;
    if (this.tokens < n) return false;
    this.tokens -= n;
    return true;
  }
}

/** Chat: strip control chars, collapse whitespace, cap length. */
export function sanitizeChat(s: unknown): string | null {
  if (typeof s !== 'string') return null;
  // eslint-disable-next-line no-control-regex
  const t = s.replace(/[\u0000-\u001f\u007f‪-‮⁦-⁩]/g, '').replace(/\s+/g, ' ').trim().slice(0, 256);
  return t.length ? t : null;
}

export function sanitizeName(s: unknown): string {
  if (typeof s !== 'string') return 'Player';
  const t = s.replace(/[^A-Za-z0-9_ .()-]/g, '').trim().slice(0, 20);
  return t.length ? t : 'Player';
}

/**
 * Violation score: each failure adds, it decays 1 point/second, and the host
 * kicks a peer above KICK_SCORE.
 */
export class Violations {
  private score = 0;
  private last = 0;
  static readonly KICK_SCORE = 40;
  add(now: number, pts: number): number {
    this.score = Math.max(0, this.score - (now - this.last) / 1000) + pts;
    this.last = now;
    return this.score;
  }
}
